import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LocalizeRouterModule } from '@gilsdav/ngx-translate-router';

import { AuthGuard } from '~/auth/guards/auth.guard';
import { OrganizerActivityStatusEnum } from '~/organizer/enums/activity-status.enum';
import { OrganizerSharedPageListComponent } from '~/organizer/shared/components/pages/list/list.component';
import { OrganizerSharedClearTimeslotGuard } from '~/organizer/shared/guards/clear-timeslot/clear-timeslot.guard';
import { OrganizerSharedGetOrganizationByQueryIdGuard } from '~/organizer/shared/guards/get-organization-by-query-id/get-organization-by-query-id.guard';
import { OrganizerSharedGetOrganizationFromAuthUserGuard } from '~/organizer/shared/guards/get-organization-from-auth-user/get-organization-from-auth-user.guard';
import { OrganizerSharedHasOrganizationGuard } from '~/organizer/shared/guards/has-organization/has-organization.guard';
import { OrganizerSharedActivityResolver } from '~/organizer/shared/resolvers/activity/activity.resolver';
import { OrganizerSharedTimeslotParticipantListByBookingIdResolver } from '~/organizer/shared/resolvers/timeslot/participant/list-by-booking-id/list-by-booking-id.resolver';
import { OrganizerSharedTimeslotParticipantSingleResolver } from '~/organizer/shared/resolvers/timeslot/participant/single/single.resolver';
import { OrganizerSharedTimeslotResolver } from '~/organizer/shared/resolvers/timeslot/timeslot.resolver';
import { OrganizerTimeslotPageManageComponent } from './components/pages/manage/manage.component';
import { OrganizerTimeslotPageParticipantListComponent } from './components/pages/participant/list/list.component';
import { OrganizerTimeslotPageParticipantManageComponent } from './components/pages/participant/manage/manage.component';
import { OrganizerTimeslotPagePreScheduleGroupSizeComponent } from './components/pages/pre-schedule/group-size/group-size.component';
import { OrganizerTimeslotPagePreScheduleLiveUrlComponent } from './components/pages/pre-schedule/live-url/live-url.component';
import { OrganizerTimeslotPagePreSchedulePlansComponent } from './components/pages/pre-schedule/plans/plans.component';
import { OrganizerTimeslotPagePreScheduleComponent } from './components/pages/pre-schedule/pre-schedule.component';
import { OrganizerTimeslotPageScheduleComponent } from './components/pages/schedule/schedule.component';

const routes: Routes = [
  {
    path: 'organizer.timeslot.schedule',
    component: OrganizerTimeslotPageScheduleComponent,
    canActivate: [
      AuthGuard,
      OrganizerSharedGetOrganizationByQueryIdGuard,
      OrganizerSharedGetOrganizationFromAuthUserGuard,
      OrganizerSharedHasOrganizationGuard,
    ],
    children: [
      {
        path: 'organizer.timeslot.published',
        component: OrganizerSharedPageListComponent,
        data: { type: OrganizerActivityStatusEnum.PUBLISHED },
      },
      {
        path: 'organizer.timeslot.unpublished',
        component: OrganizerSharedPageListComponent,
        data: { type: OrganizerActivityStatusEnum.UNPUBLISHED },
      },
      {
        path: 'organizer.timeslot.draft',
        component: OrganizerSharedPageListComponent,
        data: { type: OrganizerActivityStatusEnum.DRAFT },
      },
      {
        path: '**',
        redirectTo: 'organizer.timeslot.published',
      },
    ],
  },
  {
    path: 'organizer.timeslot.pre-schedule/:id',
    component: OrganizerTimeslotPagePreScheduleComponent,
    canActivate: [
      AuthGuard,
      OrganizerSharedGetOrganizationByQueryIdGuard,
      OrganizerSharedGetOrganizationFromAuthUserGuard,
      OrganizerSharedHasOrganizationGuard,
      OrganizerSharedClearTimeslotGuard,
    ],
    resolve: {
      activityData: OrganizerSharedActivityResolver,
    },
  },
  {
    path: 'organizer.timeslot.pre-schedule/:id/organizer.timeslot-edition.group-size',
    component: OrganizerTimeslotPagePreScheduleGroupSizeComponent,
    canActivate: [
      AuthGuard,
      OrganizerSharedGetOrganizationByQueryIdGuard,
      OrganizerSharedGetOrganizationFromAuthUserGuard,
      OrganizerSharedHasOrganizationGuard,
    ],
    resolve: {
      activityData: OrganizerSharedActivityResolver,
    },
  },
  {
    path: 'organizer.timeslot.pre-schedule/:id/organizer.timeslot-edition.offers',
    component: OrganizerTimeslotPagePreSchedulePlansComponent,
    canActivate: [
      AuthGuard,
      OrganizerSharedGetOrganizationByQueryIdGuard,
      OrganizerSharedGetOrganizationFromAuthUserGuard,
      OrganizerSharedHasOrganizationGuard,
    ],
    resolve: {
      activityData: OrganizerSharedActivityResolver,
    },
  },
  {
    path: 'organizer.timeslot.pre-schedule/:id/organizer.timeslot-edition.live-url',
    component: OrganizerTimeslotPagePreScheduleLiveUrlComponent,
    canActivate: [
      AuthGuard,
      OrganizerSharedGetOrganizationByQueryIdGuard,
      OrganizerSharedGetOrganizationFromAuthUserGuard,
      OrganizerSharedHasOrganizationGuard,
    ],
    resolve: {
      activityData: OrganizerSharedActivityResolver,
    },
  },
  {
    path: 'organizer.timeslot.manage/:id',
    component: OrganizerTimeslotPageManageComponent,
    canActivate: [
      AuthGuard,
      OrganizerSharedGetOrganizationByQueryIdGuard,
      OrganizerSharedGetOrganizationFromAuthUserGuard,
      OrganizerSharedHasOrganizationGuard,
    ],
    resolve: {
      timeslotData: OrganizerSharedTimeslotResolver,
    },
  },
  {
    path: 'organizer.timeslot.participant.list/:id',
    component: OrganizerTimeslotPageParticipantListComponent,
    canActivate: [
      AuthGuard,
      OrganizerSharedGetOrganizationByQueryIdGuard,
      OrganizerSharedGetOrganizationFromAuthUserGuard,
      OrganizerSharedHasOrganizationGuard,
    ],
    resolve: {
      timeslotData: OrganizerSharedTimeslotResolver,
      bookingItems: OrganizerSharedTimeslotParticipantListByBookingIdResolver,
    },
  },
  {
    path: 'organizer.timeslot.participant.manage/:id',
    component: OrganizerTimeslotPageParticipantManageComponent,
    canActivate: [
      AuthGuard,
      OrganizerSharedGetOrganizationByQueryIdGuard,
      OrganizerSharedGetOrganizationFromAuthUserGuard,
      OrganizerSharedHasOrganizationGuard,
    ],
    resolve: {
      timeslotData: OrganizerSharedTimeslotResolver,
      bookingItemGroupByUserId:
        OrganizerSharedTimeslotParticipantSingleResolver,
    },
  },
];

/**
 * Prefix route of the sub modules
 */
@NgModule({
  imports: [
    RouterModule.forChild(routes),
    LocalizeRouterModule.forChild(routes),
  ],
  exports: [RouterModule, LocalizeRouterModule],
})
export class OrganizerTimeslotRoutingModule {}
